import SEO from "../components/SEO";

const outlineWords = ["RELATABLE","MULTILINGUAL","YOUTH-FOCUSED","COMMUNITY-ROOTED","IMPACTFUL"];

export default function OurWhy() {
  return (
    <>
    <SEO
      title="Our Why"
      path="/our-why"
      description="Health information should not feel far away or confusing. The Health Plug exists to create health content that sounds like the people it is made for — multilingual, multimedia, community-rooted and easy to understand."
    />
    <div style={{ background: "#0B0B0B", paddingTop: "5rem" }}>
      {/* Hero */}
      <section style={{ padding: "6rem 2rem 4rem", position: "relative", overflow: "hidden", minHeight: "60vh", display: "flex", alignItems: "center" }}>
        {/* Outline background words */}
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
          {outlineWords.map((w, i) => (
            <span key={i} style={{
              position: "absolute",
              fontFamily: "var(--font)", fontWeight: 900,
              fontSize: "clamp(4rem, 10vw, 10rem)",
              color: "transparent",
              WebkitTextStroke: "1.5px rgba(149,187,131,0.12)",
              top: `${i * 20}%`,
              left: `${(i % 2 === 0 ? -2 : 30)}%`,
              whiteSpace: "nowrap",
              userSelect: "none",
            }}>{w}</span>
          ))}
        </div>

        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <p className="section-label" style={{ color: "#95BB83" }}>Our Why</p>
          <h1 style={{ fontFamily: "var(--font)", fontWeight: 900, fontSize: "clamp(3rem, 7vw, 6rem)", color: "#fff", lineHeight: 0.95, letterSpacing: "-0.03em", marginBottom: "2rem" }}>
            Our<br /><span style={{ color: "#8A1613" }}>Why.</span>
          </h1>
        </div>
      </section>

      {/* Main copy */}
      <section style={{ background: "#F7F1DF", padding: "6rem 2rem", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
          {outlineWords.map((w, i) => (
            <span key={i} style={{
              position: "absolute",
              fontFamily: "var(--font)", fontWeight: 900,
              fontSize: "clamp(5rem, 12vw, 14rem)",
              color: "transparent",
              WebkitTextStroke: "1.5px rgba(138,22,19,0.06)",
              top: `${i * 22}%`,
              right: `${(i % 2 === 0 ? -5 : 20)}%`,
              whiteSpace: "nowrap",
              userSelect: "none",
            }}>{w}</span>
          ))}
        </div>

        <div className="container-sm" style={{ position: "relative", zIndex: 1 }}>
          <p style={{ fontFamily: "var(--font-body)", fontSize: "1.15rem", color: "#0B0B0B", lineHeight: 1.85, marginBottom: "2rem" }}>
            Health information should not feel far away, confusing or only available in English.
          </p>
          <p style={{ fontFamily: "var(--font-body)", fontSize: "1.15rem", color: "#0B0B0B", lineHeight: 1.85, marginBottom: "2rem" }}>
            Across South Africa, many young people face real health challenges, from HIV, mental health and sexual health to misinformation, stigma and barriers in the public health system. But too often, health communication is too technical, too formal and disconnected from young people's lived experiences.
          </p>
          <p style={{ fontFamily: "var(--font-body)", fontSize: "1.15rem", color: "#0B0B0B", lineHeight: 1.85, marginBottom: "3rem" }}>
            The Health Plug exists to change that. We create health content that sounds like the people it is made for, multilingual, multimedia, community-rooted and easy to understand. Our work blends journalism, storytelling, pop culture and impact communication to help young people know their bodies, understand their rights and participate in the health systems meant to serve them.
          </p>

          {/* Pull quote */}
          <blockquote style={{ borderLeft: "4px solid #8A1613", paddingLeft: "1.5rem", margin: "0 0 3rem" }}>
            <p style={{ fontFamily: "var(--font)", fontWeight: 800, fontSize: "clamp(1.3rem, 3vw, 2rem)", color: "#8A1613", lineHeight: 1.3 }}>
              "When young people are healthy, communities are healthy.<br />When young people are informed, generations are transformed."
            </p>
          </blockquote>

          {/* Pillars */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem" }} className="mobile-full">
            {[
              {
                icon: (
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="#0B0B0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="6" width="24" height="18" rx="3"/>
                    <path d="M10 14h12M10 19h8M4 24l-2 4h28l-2-4"/>
                    <path d="M30 10h4a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-4"/>
                  </svg>
                ),
                word: "Multilingual", desc: "Health information in the language you live in — not just English."
              },
              {
                icon: (
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="#0B0B0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="20" cy="20" r="15"/>
                    <polygon points="16,13 30,20 16,27" fill="none"/>
                  </svg>
                ),
                word: "Multimedia", desc: "Podcasts, videos, posters, radio and social content that meets young people where they are."
              },
              {
                icon: (
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="#0B0B0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="20" cy="12" r="5"/>
                    <path d="M10 36v-4a10 10 0 0 1 20 0v4"/>
                    <circle cx="8" cy="18" r="4"/>
                    <path d="M2 36v-3a8 8 0 0 1 10-7"/>
                    <circle cx="32" cy="18" r="4"/>
                    <path d="M38 36v-3a8 8 0 0 0-10-7"/>
                  </svg>
                ),
                word: "Community-Rooted", desc: "Created with and for communities, not pushed down from above."
              },
              {
                icon: (
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="#0B0B0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="22,4 8,24 20,24 18,36 32,16 20,16"/>
                  </svg>
                ),
                word: "Impactful", desc: "Measured, evidence-driven communications that lead to real behavioural change."
              },
            ].map((p, i) => (
              <div key={i} style={{ background: "#fff", borderRadius: 16, padding: "1.75rem", border: "1px solid rgba(11,11,11,0.07)" }}>
                <div style={{ marginBottom: "0.75rem" }}>{p.icon}</div>
                <h3 style={{ fontFamily: "var(--font)", fontWeight: 800, fontSize: "1.1rem", color: "#8A1613", marginBottom: "0.4rem" }}>{p.word}</h3>
                <p style={{ fontFamily: "var(--font-body)", color: "rgba(11,11,11,0.55)", fontSize: "0.9rem", lineHeight: 1.65 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
